import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';
import { getCsrfToken } from '../../../config/csrf';

const EmployerContext = createContext(null);

/* Backend wiring for the employer_admin self-service portal, scoped to
   BrandonUserStoryAPI.md's EMP-01 through EMP-04 (README.md §4.4 for the
   underlying endpoint table). Same db / refresh() / fetch-helper pattern as
   icib-admin/context/IcibAdminContext.js and employee/context/EmployeeContext.js.

   All four EMP stories are wired up now, each under its own `// ==== EMP-XX`
   section below. EMP-01 is the same summary-list + by-id drill-down split
   ICIB-01 uses. EMP-02's drill-down (EMP-02b) and EMP-04's removal target
   both reuse EMP-01's employeeDetails/loadEmployeeDetail unchanged, since
   every one of these stories shares the same GET .../employees/{id}
   endpoint. EMP-03 (new starter) is the same POST /companies/{gid}/employees
   ICIB-02a's Addition uses, just employer_admin-scoped, plus the
   groupProducts/activeTemplates reads that form needs. EMP-04 (remove
   enrolment) reuses the same bulk-upload endpoint as EMP-03, just with
   REMOVAL-shaped rows instead of ADDITION ones — BrandonUserStoryAPI.md's
   EMP-04 is explicit that there is no separate single-item removal
   endpoint; a single removal is simply a one-row bulk-upload file. Touch
   only the section for the story you're working on, same convention as
   IcibAdminContext's ICIB-XX sections.

   Group scoping: unlike icib_admin, an employer_admin has no `GET /companies`
   (icib_admin-only, README §4.3) to browse — so there's no "All groups"
   option here, and no way to discover which group(s) this employer_admin
   manages except via their own `GET /users` row. `GET /users`, called as
   employer_admin, is automatically scoped to the group(s) they hold via
   `UserEmployerClient` (ICIB-08l) and returns every employer_admin assigned
   to those groups, each carrying `assigned_companies` (company_id/company_code/
   job_title) — including the caller's own row. `assigned_companies` doesn't
   include a group's display `name`, so each assigned group is then resolved
   via `GET /companies/{id}` (the one group-detail endpoint employer_admin can
   call, for their own group only) to get the real name/status. */

const EMPTY_DB = {
  me: null,
  // This employer_admin's own assigned groups only — resolved via GET
  // /users + GET /companies/{id}, see refresh() below. Never "every group" —
  // there's no such view for this role.
  groups: [],
  errors: {},
};

// Same CSRF pattern as the other two contexts — Django's CsrfViewMiddleware
// rejects any unsafe-method request without a matching X-CSRFToken header.
// Not read from document.cookie — the SPA and API are on different
// registrable domains in every deployed environment, so this page's JS can
// never see the API's csrftoken cookie. getCsrfToken() (config/csrf.js)
// holds the token AuthContext fetched from GET /auth/csrf instead.
async function employerFetch(path, { method = 'GET', body } = {}) {
  let res;
  try {
    const unsafe = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    // Omit the header entirely when there's no token yet, rather than sending
    // '' — an empty X-CSRFToken header fails Django's length check ("incorrect
    // length") instead of the clearer "CSRF token missing" an absent header gets.
    const csrfToken = unsafe ? getCsrfToken() : null;
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(csrfToken ? { 'X-CSRFToken': csrfToken } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    return { error: 'network_error', message: 'Could not reach the server. Check your connection and try again.' };
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) return data || { error: 'request_failed', message: `Request failed (${res.status}).` };
  return data;
}

// Defensive read for a list response, same reasoning as the other two
// contexts' `arr` helper — accept either a bare array or the documented
// envelope object.
function arr(data, ...keys) {
  if (Array.isArray(data)) return data;
  for (const k of keys) if (Array.isArray(data?.[k])) return data[k];
  return [];
}

// EMP-01a's own endpoint (GET /companies/{company_id}/employees/summary) shares
// its limit/offset pagination contract with GET /notifications and GET
// /users (ICIB-08l, default limit 50, capped 200) — walk every page so the
// result is always the complete roster, same as IcibAdminContext's
// fetchAllPages. Dedupes by id across pages defensively, same reasoning.
async function fetchAllPages(path, itemsKey) {
  const sep = path.includes('?') ? '&' : '?';
  const limit = 200;
  let offset = 0;
  let items = [];
  const seenIds = new Set();
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const res = await employerFetch(`${path}${sep}limit=${limit}&offset=${offset}`);
    if (res?.error) {
      if (offset === 0) return res;
      break;
    }
    const page = arr(res, itemsKey);
    let newCount = 0;
    for (const item of page) {
      const id = item?.id ?? item?.employee_id ?? item?.user_id;
      if (id != null) {
        if (seenIds.has(id)) continue;
        seenIds.add(id);
      }
      items.push(item);
      newCount += 1;
    }
    const count = typeof res?.count === 'number' ? res.count : items.length;
    if (page.length < limit || items.length >= count || !page.length || !newCount) break;
    offset += limit;
  }
  return { [itemsKey]: items, count: items.length };
}

export function EmployerProvider({ children }) {
  const [db, setDb] = useState(EMPTY_DB);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [inFlightCount, setInFlightCount] = useState(0);
  const [groupId, setGroupId] = useState(null);
  const requestIdRef = useRef(0);
  // EMP-01a — GET /companies/{gid}/employees/summary. Lazy, per group, same
  // reasoning as IcibAdminContext's loadEmployees — called by whichever
  // view's mount effect needs the roster, so it refetches correctly on a
  // group switch.
  const [employees, setEmployees] = useState([]);
  const [employeesStatus, setEmployeesStatus] = useState('idle');
  const [employeesError, setEmployeesError] = useState(null);
  const loadRequestIdRef = useRef({ employees: 0, outstanding: 0, pendingIntake: 0 });
  const loadedGroupRef = useRef({ employees: undefined, outstanding: undefined, pendingIntake: undefined });
  // EMP-01b — GET /companies/{gid}/employees/{id}. employee_id -> result
  // (form_data + enrolments), same on-demand cache pattern as
  // IcibAdminContext's employeeDetails.
  const [employeeDetails, setEmployeeDetails] = useState({});
  // EMP-02a — GET /companies/{gid}/employees/summary?enrolment_status=invited.
  // Kept separate from `employees` above (own state/status/error/loadedGroupRef
  // entry) rather than refiltering the EMP-01a roster client-side — EMP-01a
  // is often never loaded at all when only Outstanding is visited, and the
  // two lists are independently paginated over the wire (fetchAllPages),
  // so there's nothing to reuse by combining them.
  const [outstanding, setOutstanding] = useState([]);
  const [outstandingStatus, setOutstandingStatus] = useState('idle');
  const [outstandingError, setOutstandingError] = useState(null);
  // EMP-07 — GET /companies/{gid}/overview. Same endpoint/shape as ICIB-15,
  // own group only (server-enforced — a group this employer_admin isn't
  // assigned to 403s). Not paginated — a single summary object per group,
  // cached by gid.
  const [groupOverview, setGroupOverview] = useState({});
  const [groupOverviewStatus, setGroupOverviewStatus] = useState('idle');
  const [groupOverviewError, setGroupOverviewError] = useState(null);
  // ICIB-13a (employer_admin scope) — GET /companies/{gid}/pending-intake.
  // Lazy, per group, same pattern as `employees` above.
  const [pendingIntake, setPendingIntake] = useState([]);
  const [pendingIntakeStatus, setPendingIntakeStatus] = useState('idle');
  const [pendingIntakeError, setPendingIntakeError] = useState(null);

  /* Loads this employer_admin's own profile and their assigned group(s) —
     see the module comment above for why this takes two round trips
     (GET /users to find `assigned_companies`, then GET /companies/{id} per
     assigned group for its display name) — plus, for whichever group this
     refresh resolves to, that group's products (EMP-03's GET
     /companies/{gid}/products). Folded into this same batch rather than left
     as a separate per-view lazy loader (loadGroupProducts below still
     exists for an on-demand refetch, e.g. after a mutation elsewhere) —
     the lazy version left a real gap: LeaversView didn't call it, so
     landing on Remove enrolment directly (e.g. after a page refresh)
     without visiting NewStarterView/EmployeesView/OutstandingView first
     left groupProducts empty and enrolment product names fell back to
     showing raw ids. Same strategy as IcibAdminContext.refresh, which
     fetches groupProducts for the resolved gid in its own batch for the
     same reason. Runs on mount and on every group switch (the effect
     below fires it whenever `groupId` changes) — unlike before, when it
     ran once on mount only, since there was nothing group-scoped in this
     batch to refetch on a switch. */
  const refresh = useCallback((gidOverride) => {
    const requestId = ++requestIdRef.current;
    setStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    setInFlightCount((c) => c + 1);
    return Promise.all([
      employerFetch('/auth/me'),
      employerFetch('/users?role=employer_admin'),
    ]).then(async ([me, usersRes]) => {
      const errors = {};
      if (usersRes?.error) errors.groups = usersRes.message || usersRes.error;

      const myRow = arr(usersRes, 'users').find((u) => u.id === me?.id);
      const assigned = myRow?.assigned_companies || [];
      const groupResults = await Promise.all(assigned.map((g) => employerFetch(`/companies/${g.company_id}`)));
      const groups = assigned.map((g, i) => {
        const full = groupResults[i];
        return full && !full.error
          ? { id: full.id, company_code: full.company_code, name: full.name, status: full.status, job_title: g.job_title }
          : { id: g.company_id, company_code: g.company_code, name: g.company_code, status: 'active', job_title: g.job_title };
      });

      // No "All groups" default here (see module comment) — an explicit
      // gidOverride (a scope-selector switch) wins, otherwise keep the
      // current selection if it's still valid, otherwise fall back to the
      // first assigned group. Resolved here, before the group-scoped
      // products fetch below, so both land in the same setState batch.
      const gid = gidOverride !== undefined
        ? gidOverride
        : (groupId && groups.some((g) => g.id === groupId) ? groupId : (groups[0]?.id || null));

      let groupProducts = [];
      if (gid) {
        const gpRes = await employerFetch(`/companies/${gid}/products`);
        if (gpRes?.error) errors.groupProducts = gpRes.message || gpRes.error;
        else groupProducts = arr(gpRes, 'group_products', 'products').map((gp) => ({ ...gp, company_id: gid, id: gp.id || gp.product_id }));
      }

      if (requestIdRef.current !== requestId) return null;

      setDb({ me: me && !me.error ? me : null, groups, errors });
      setGroupId(gid);
      setGroupProducts(groupProducts);
      setGroupProductsStatus('ready');
      setGroupProductsError(errors.groupProducts || null);
      setStatus('ready');
      return null;
    }).catch((err) => {
      if (requestIdRef.current !== requestId) return null;
      console.error('Employer dashboard load threw unexpectedly', err);
      setStatus('error');
      return { error: 'unexpected_error', message: 'Something went wrong loading the dashboard.' };
    }).finally(() => {
      setInFlightCount((c) => c - 1);
    });
  }, [groupId]);

  // Loads everything on mount, and again whenever `refresh` itself changes
  // identity — which happens exactly when `groupId` does, since `refresh`
  // depends on it above. Deliberately calls refresh() with no override
  // here (unlike IcibAdminContext's mount effect, which always passes
  // `groupId` explicitly) — passing `groupId` here would defeat the
  // "auto-pick the first assigned group" fallback inside refresh() on the
  // very first call, since `groupId` starts at `null` and `null !==
  // undefined`, so the override branch would always win and never fall
  // through to `groups[0]?.id`. Leaving it out lets refresh() re-derive
  // gid itself off the *current* groupId each time — correct both for the
  // initial "nothing selected yet" resolution and for every later
  // scope-selector switch (setGroupId(newGid) changes groupId, which
  // recreates refresh, which reruns this effect). Bounded to at most one
  // extra self-triggered call right after the initial resolution settles
  // groupId from null to a real id (same one extra round trip
  // IcibAdminContext.refresh's own mount effect documents and accepts) —
  // requestIdRef's stale-response guard above discards anything superseded.
  useEffect(() => { refresh(); }, [refresh]);

  // ==================================================================== EMP-01
  // EMP-01a — GET /companies/{gid}/employees/summary. Deliberately lightweight
  // — employee_id + nested user only, no form_data/enrolments; EMP-01b
  // (loadEmployeeDetail below) is the per-person drill-down for those.
  const loadEmployees = useCallback(async (gid) => {
    const requestId = ++loadRequestIdRef.current.employees;
    if (!gid) { setEmployees([]); setEmployeesStatus('ready'); setEmployeesError(null); loadedGroupRef.current.employees = null; return null; }
    const sameGroup = loadedGroupRef.current.employees === gid;
    setEmployeesStatus((s) => (sameGroup && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/companies/${gid}/employees/summary`, 'employees');
    if (loadRequestIdRef.current.employees !== requestId) return res;
    if (res?.error) {
      setEmployeesStatus('error');
      setEmployeesError(res.message || res.error);
      return res;
    }
    setEmployees(arr(res, 'employees').map((e) => ({ ...e, id: e.employee_id || e.id, company_id: gid })));
    setEmployeesStatus('ready');
    setEmployeesError(null);
    loadedGroupRef.current.employees = gid;
    return res;
  }, []);

  const employeesIn = (gid) => employees.filter((e) => e.company_id === gid);

  // EMP-01b — GET /companies/{company_id}/employees/{id}. Cached — call again
  // to force a refetch.
  async function loadEmployeeDetail(gid, employeeId) {
    const res = await employerFetch(`/companies/${gid}/employees/${employeeId}`);
    if (!res?.error) {
      const normalized = { ...res, enrolments: (res.enrolments || []).map((en) => ({ ...en, id: en.enrolment_id || en.id })) };
      setEmployeeDetails((m) => ({ ...m, [employeeId]: normalized }));
      return normalized;
    }
    return res;
  }

  // ==================================================================== EMP-02
  // EMP-02a — GET /companies/{gid}/employees/summary?enrolment_status={status}.
  // Same endpoint/shape as EMP-01a's loadEmployees, just the documented
  // `enrolment_status` filter added — narrows *which employees appear*
  // (anyone with at least one enrolment in the given status), not what's
  // returned per employee (still employee_id + user only, no form_data/
  // enrolments — the drill-down for those is EMP-02b, which reuses
  // loadEmployeeDetail/employeeDetails above unchanged, since it's the same
  // GET .../employees/{id} endpoint as EMP-01b). `status` is one of the five
  // documented EmployeeEnrolment.Status values — defaults to `invited` to
  // match this story's original "who hasn't completed enrolment" framing;
  // the OutstandingView dropdown lets the caller pick a different one.
  const loadOutstanding = useCallback(async (gid, status = 'invited') => {
    const requestId = ++loadRequestIdRef.current.outstanding;
    if (!gid) { setOutstanding([]); setOutstandingStatus('ready'); setOutstandingError(null); loadedGroupRef.current.outstanding = null; return null; }
    const key = `${gid}:${status}`;
    const sameKey = loadedGroupRef.current.outstanding === key;
    setOutstandingStatus((s) => (sameKey && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/companies/${gid}/employees/summary?enrolment_status=${status}`, 'employees');
    if (loadRequestIdRef.current.outstanding !== requestId) return res;
    if (res?.error) {
      setOutstandingStatus('error');
      setOutstandingError(res.message || res.error);
      return res;
    }
    setOutstanding(arr(res, 'employees').map((e) => ({ ...e, id: e.employee_id || e.id, company_id: gid })));
    setOutstandingStatus('ready');
    setOutstandingError(null);
    loadedGroupRef.current.outstanding = key;
    return res;
  }, []);

  const outstandingIn = (gid) => outstanding.filter((e) => e.company_id === gid);

  // ==================================================================== EMP-07
  // GET /companies/{gid}/overview (BrandonUserStoryAPI.md EMP-07) — same
  // endpoint ICIB-15 documents, `total_employees`/`fully_onboarded_employees`/
  // `employees_with_invited_enrolment`/`employees_with_removed_enrolment`
  // for this employer_admin's own group.
  const loadGroupOverview = useCallback(async (gid) => {
    if (!gid) { setGroupOverviewStatus('ready'); setGroupOverviewError(null); return null; }
    setGroupOverviewStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    const res = await employerFetch(`/companies/${gid}/overview`);
    if (res?.error) {
      setGroupOverviewStatus('error');
      setGroupOverviewError(res.message || res.error);
      return res;
    }
    setGroupOverview((m) => ({ ...m, [gid]: res }));
    setGroupOverviewStatus('ready');
    setGroupOverviewError(null);
    return res;
  }, []);
  // ==================================================================== EMP-03
  // GET /companies/{gid}/products — icib_admin, employer_admin (own group only,
  // README §4.6). Always active-only for employer_admin (the include_inactive/
  // is_active params are ignored for this role server-side, per §4.6), which
  // is exactly what the New starter form needs: how many active products this
  // group currently offers, since an Addition enrols across all of them
  // (§2.8) with no product picker.
  const [groupProducts, setGroupProducts] = useState([]);
  const [groupProductsStatus, setGroupProductsStatus] = useState('idle');
  const [groupProductsError, setGroupProductsError] = useState(null);

  const loadGroupProducts = useCallback(async (gid) => {
    if (!gid) { setGroupProducts([]); setGroupProductsStatus('ready'); setGroupProductsError(null); return null; }
    setGroupProductsStatus((s) => (s === 'ready' ? 'ready' : 'loading'));
    const res = await employerFetch(`/companies/${gid}/products`);
    if (res?.error) {
      setGroupProductsStatus('error');
      setGroupProductsError(res.message || res.error);
      return res;
    }
    setGroupProducts(arr(res, 'group_products', 'products').map((gp) => ({ ...gp, company_id: gid, id: gp.id || gp.product_id })));
    setGroupProductsStatus('ready');
    setGroupProductsError(null);
    return res;
  }, []);

  const groupProductsIn = (gid) => groupProducts.filter((gp) => gp.company_id === gid);

  // GET /companies/{gid}/form-templates?active=true (README §4.5, icib_admin/
  // employer_admin own-group-only) — the group's active template *with
  // fields*, so the New starter form can collect the same group-specific
  // form_data the intake pipeline expects (§2.6), not just the five fixed
  // profile fields. 404 not_found (a group with no active template yet) is
  // stored as `null`, not surfaced as an error — same convention as
  // IcibAdminContext's loadActiveTemplate.
  const [activeTemplates, setActiveTemplates] = useState({});
  const loadActiveTemplate = useCallback(async (gid) => {
    const res = await employerFetch(`/companies/${gid}/form-templates?active=true`);
    if (res?.error === 'not_found') { setActiveTemplates((m) => ({ ...m, [gid]: null })); return res; }
    if (!res?.error) setActiveTemplates((m) => ({ ...m, [gid]: res.template }));
    return res;
  }, []);

  // EMP-03 — POST /companies/{company_id}/employees (README §2.6 Operation 1 +
  // Operation 2 ADDITION branch; same underlying operation as ICIB-02a,
  // just employer_admin-scoped to their own group). A future `effective_date`
  // (§2.6a) returns `202 scheduled` instead of creating anything yet — refetch
  // `employees` only on the immediate-effect path, since a scheduled row has
  // nothing new in the roster to show. No pending-intake tracking here (out
  // of EMP-03's scope) — the scheduled case is surfaced inline via the toast
  // this call's caller shows, not a separate list.
  async function addOne(fields) {
    const res = await employerFetch(`/companies/${groupId}/employees`, { method: 'POST', body: fields });
    if (!res?.error && res?.status !== 'scheduled') await loadEmployees(groupId);
    return res;
  }

  /* EMP-03 bulk — POST /companies/{company_id}/employees/bulk-upload, same
     endpoint/contract as ICIB-02a's bulk path (BrandonUserStoryAPI.md
     EMP-03), just employer_admin-scoped to their own group. All-or-nothing
     per row (README §2.6) — `rows` is whatever parseCsv/parseXlsx produced,
     keyed by the same headers the downloaded template uses. Refetches
     `employees` only, same as addOne above — this context has no
     pending-intake tracking, so a scheduled row's toast is the only place
     that surfaces. */
  const rateLog = useRef([]);
  function rateLimited() {
    const cutoff = Date.now() - 60000;
    rateLog.current = rateLog.current.filter((t) => t > cutoff);
    if (rateLog.current.length >= 10) return true;
    rateLog.current.push(Date.now());
    return false;
  }
  async function bulkUploadEmployees(fileName, rows) {
    if (rateLimited()) return { error: 'rate_limited', message: 'Too many bulk-upload requests. Limit is 10 per minute per user.' };
    const res = await employerFetch(`/companies/${groupId}/employees/bulk-upload`, { method: 'POST', body: { file: fileName, rows } });
    if (!res?.error) await loadEmployees(groupId);
    return res;
  }

  // ==================================================================== EMP-04
  // The mutation itself is the REMOVAL branch of the same POST
  // /companies/{company_id}/employees/bulk-upload endpoint EMP-03's
  // bulkUploadEmployees above already wires up — see that comment.
  //
  // The "person" picker needs to know which employees still have an
  // active enrolment to remove. EMP-01a's summary (`employees` above) now
  // carries `has_active_enrolment` per employee directly
  // (BrandonUserStoryAPI.md ICIB-01a), so LeaversView filters on that field
  // straight off the roster fetch — no more EMP-01b drill-down per employee
  // just to answer that question.

  // ============================================================ ICIB-13 (EMP)
  // ICIB-13a — GET /companies/{gid}/pending-intake. BrandonUserStoryAPI.md
  // ICIB-13 documents both operations for `icib_admin` (any company) and
  // `employer_admin` (own company only) — same endpoints IcibAdminContext's
  // loadPendingIntake/cancelPendingIntake use, just scoped here to whichever
  // of this employer_admin's own groups is selected (the scope selector only
  // ever offers their assigned groups, and the server 403s anything else).
  // Every PendingIntakeRow regardless of status, server-ordered
  // soonest-apply_on-first, paginated (limit/offset) like EMP-01a.
  const loadPendingIntake = useCallback(async (gid) => {
    const requestId = ++loadRequestIdRef.current.pendingIntake;
    if (!gid) { setPendingIntake([]); setPendingIntakeStatus('ready'); setPendingIntakeError(null); loadedGroupRef.current.pendingIntake = null; return null; }
    const sameGroup = loadedGroupRef.current.pendingIntake === gid;
    setPendingIntakeStatus((s) => (sameGroup && s === 'ready' ? 'ready' : 'loading'));
    const res = await fetchAllPages(`/companies/${gid}/pending-intake`, 'pending_intake_rows');
    if (loadRequestIdRef.current.pendingIntake !== requestId) return res;
    if (res?.error) {
      setPendingIntakeStatus('error');
      setPendingIntakeError(res.message || res.error);
      return res;
    }
    setPendingIntake(arr(res, 'pending_intake_rows'));
    setPendingIntakeStatus('ready');
    setPendingIntakeError(null);
    loadedGroupRef.current.pendingIntake = gid;
    return res;
  }, []);

  // ICIB-13b — DELETE /companies/{gid}/pending-intake/{id}. Soft-delete
  // (status -> cancelled); only a still-`pending` row can be cancelled
  // (409 invalid_state otherwise, surfaced via the caller's toast).
  // Refetches the list either way so the row's real status shows.
  async function cancelPendingIntake(gid, pendingIntakeRowId) {
    const res = await employerFetch(`/companies/${gid}/pending-intake/${pendingIntakeRowId}`, { method: 'DELETE' });
    await loadPendingIntake(gid);
    return res;
  }

  const group = (id) => db.groups.find((g) => g.id === id);
  const curGroup = () => group(groupId);

  const refreshing = inFlightCount > 0;

  const value = {
    db, status, refresh, refreshing, groupId, setGroupId, group, curGroup,
    employees, employeesStatus, employeesError, loadEmployees, employeesIn,
    employeeDetails, loadEmployeeDetail,
    outstanding, outstandingStatus, outstandingError, loadOutstanding, outstandingIn,
    groupProducts, groupProductsStatus, groupProductsError, loadGroupProducts, groupProductsIn,
    activeTemplates, loadActiveTemplate, addOne,
    bulkUploadEmployees,
    groupOverview, groupOverviewStatus, groupOverviewError, loadGroupOverview,
    pendingIntake, pendingIntakeStatus, pendingIntakeError, loadPendingIntake, cancelPendingIntake,
  };

  return <EmployerContext.Provider value={value}>{children}</EmployerContext.Provider>;
}

export function useEmployer() {
  const ctx = useContext(EmployerContext);
  if (!ctx) throw new Error('useEmployer must be used within an EmployerProvider');
  return ctx;
}
